import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import {
  Box,
  SimpleGrid,
  Text,
  Skeleton,
  useColorModeValue,
  Flex,
  Icon,
} from "@chakra-ui/react";
import {
  FaArrowUp,
  FaArrowDown,
  FaChartLine,
  FaCalendarAlt,
  FaExchangeAlt,
  FaBoxes,
  FaClock,
  FaBan,
} from "react-icons/fa";

// --- Formatting helpers ---

const formatCurrency = (value) => {
  const num = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (num >= 1_000_000) return `${sign}R${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${sign}R${(num / 1_000).toFixed(0)}K`;
  return `${sign}R${num.toFixed(0)}`;
};

const formatUnits = (value) => {
  const num = Math.abs(value);
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return `${num}`;
};

const formatPercentage = (value) => {
  return `${value > 0 ? "+" : ""}${value.toFixed(1)}%`;
};

const formatDays = (value) => {
  return `${Math.round(value)} days`;
};

// --- Count-up animation hook ---

const useCountUp = (target, duration = 800, isLoading = false) => {
  const [value, setValue] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    if (isLoading) {
      setValue(0);
      return;
    }
    const startTime = performance.now();
    const startVal = 0;

    const animate = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // cubic ease-out
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(startVal + (target - startVal) * eased);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      }
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [target, duration, isLoading]);

  return value;
};

// --- Individual KPI Card ---

const KpiCard = ({ title, value, subtitle, icon, accentGradient, trend, isLoading, index = 0 }) => {
  const cardBg = useColorModeValue("rgba(255,255,255,0.6)", "rgba(45,55,72,0.6)");
  const borderColor = useColorModeValue("rgba(255,255,255,0.4)", "rgba(255,255,255,0.1)");
  const titleColor = useColorModeValue("gray.500", "gray.400");
  const valueColor = useColorModeValue("gray.800", "white");
  const subtitleColor = useColorModeValue("gray.500", "gray.400");

  if (isLoading) {
    return <Skeleton height="130px" borderRadius="xl" />;
  }

  return (
    <Box
      position="relative"
      bg={cardBg}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="xl"
      p={5}
      overflow="hidden"
      backdropFilter="blur(12px)"
      transition="all 0.3s ease"
      opacity={0}
      sx={{
        animation: `kpiFadeIn 0.5s ease-out ${index * 100}ms forwards`,
        "@keyframes kpiFadeIn": {
          from: { opacity: 0, transform: "translateY(20px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
        "&:hover .accent-bar": {
          width: "5px",
        },
        "&:hover .kpi-value": {
          transform: "scale(1.05)",
        },
      }}
      _hover={{
        transform: "scale(1.02)",
        boxShadow: "0 8px 30px rgba(88, 182, 192, 0.25)",
      }}
    >
      {/* Left accent bar */}
      <Box
        className="accent-bar"
        position="absolute"
        top="0"
        left="0"
        bottom="0"
        width="4px"
        bgGradient={accentGradient}
        borderLeftRadius="xl"
        transition="width 0.3s ease"
      />

      <Flex justify="space-between" align="flex-start">
        <Box pl={2}>
          <Flex align="center" gap={1.5} mb={1}>
            <Icon as={icon} boxSize={3.5} color={titleColor} />
            <Text fontSize="xs" fontWeight="medium" color={titleColor} textTransform="uppercase" letterSpacing="wide">
              {title}
            </Text>
          </Flex>

          <Text
            className="kpi-value"
            fontSize="2xl"
            fontWeight="bold"
            color={valueColor}
            lineHeight="1.2"
            transition="transform 0.2s ease"
            display="inline-block"
          >
            {value}
          </Text>

          {subtitle && (
            <Text fontSize="xs" color={subtitleColor} mt={1}>
              {subtitle}
            </Text>
          )}
        </Box>

        {trend !== undefined && trend !== null && (
          <Flex
            align="center"
            gap={0.5}
            px={2}
            py={0.5}
            borderRadius="full"
            bg={trend >= 0 ? "green.50" : "red.50"}
            _dark={{
              bg: trend >= 0 ? "green.900" : "red.900",
            }}
          >
            <Icon
              as={trend >= 0 ? FaArrowUp : FaArrowDown}
              boxSize={2.5}
              color={trend >= 0 ? "green.500" : "red.500"}
              sx={{
                animation: "trendPulse 2s ease-in-out infinite",
                "@keyframes trendPulse": {
                  "0%, 100%": { transform: "scale(1)" },
                  "50%": { transform: "scale(1.3)" },
                },
              }}
            />
            <Text
              fontSize="xs"
              fontWeight="bold"
              color={trend >= 0 ? "green.600" : "red.600"}
              _dark={{
                color: trend >= 0 ? "green.300" : "red.300",
              }}
            >
              {Math.abs(trend).toFixed(1)}%
            </Text>
          </Flex>
        )}
      </Flex>
    </Box>
  );
};

// --- Main KpiCards component ---

const KpiCards = ({ supplier, retailer }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/rep_sales/kpi_cards`,
          { supplier, retailer, userid: localStorage.getItem("userid") },
          { signal: controller.signal }
        );
        if (res.data.status === 1) {
          setData(res.data.data);
        }
      } catch (err) {
        if (!axios.isCancel(err)) console.error("Error fetching KPI cards", err);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    fetchData();
    return () => controller.abort();
  }, [supplier, retailer]);

  const salesMtd = useCountUp(data?.salesMtd?.amount || 0, 800, loading);
  const prevMonth = useCountUp(data?.previousMonth?.amount || 0, 800, loading);
  const yoyPct = useCountUp(data?.yoyChange?.percentage || 0, 800, loading);
  const sohVal = useCountUp(data?.sohValue?.value || 0, 800, loading);
  const avgDays = useCountUp(data?.avgSdcDays?.days || 0, 800, loading);
  const sohDiscVal = useCountUp(data?.sohValueDiscontinued?.value || 0, 800, loading);

  const showDiscontinued = data?.sohValueDiscontinued != null;
  const cardCount = showDiscontinued ? 6 : 5;

  return (
    <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: cardCount }} spacing={4}>
      <KpiCard
        title="Sales MTD"
        value={formatCurrency(salesMtd)}
        subtitle={`${formatUnits(data?.salesMtd?.units || 0)} units`}
        icon={FaChartLine}
        accentGradient="linear(to-b, #58b6c0, #04aec1)"
        isLoading={loading}
        index={0}
      />
      <KpiCard
        title="Previous Month"
        value={formatCurrency(prevMonth)}
        subtitle={`${formatUnits(data?.previousMonth?.units || 0)} units`}
        icon={FaCalendarAlt}
        accentGradient="linear(to-b, #04aec1, #58b6c0)"
        isLoading={loading}
        index={1}
      />
      <KpiCard
        title="YoY Change"
        value={formatPercentage(yoyPct)}
        subtitle={`LY: ${formatCurrency(data?.yoyChange?.lastYearAmount || 0)}`}
        icon={FaExchangeAlt}
        accentGradient={
          (data?.yoyChange?.percentage || 0) >= 0
            ? "linear(to-b, #48BB78, #38A169)"
            : "linear(to-b, #FC8181, #E53E3E)"
        }
        trend={data?.yoyChange?.percentage}
        isLoading={loading}
        index={2}
      />
      <KpiCard
        title="SOH Value"
        value={formatCurrency(sohVal)}
        subtitle={`${formatUnits(data?.sohValue?.units || 0)} units`}
        icon={FaBoxes}
        accentGradient="linear(to-b, #ED8936, #DD6B20)"
        isLoading={loading}
        index={3}
      />
      <KpiCard
        title="Avg Days Cover"
        value={formatDays(avgDays)}
        subtitle={data?.dates?.display_date ? `As at ${data.dates.display_date}` : ""}
        icon={FaClock}
        accentGradient="linear(to-b, #9F7AEA, #805AD5)"
        isLoading={loading}
        index={4}
      />
      {showDiscontinued && (
        <KpiCard
          title="SOH Discontinued"
          value={formatCurrency(sohDiscVal)}
          subtitle={`${formatUnits(data?.sohValueDiscontinued?.units || 0)} units`}
          icon={FaBan}
          accentGradient="linear(to-b, #FC8181, #E53E3E)"
          isLoading={loading}
          index={5}
        />
      )}
    </SimpleGrid>
  );
};

export default KpiCards;
